/** @file App shell for the Agent Hub command center */

import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Drawer, Box, IconButton, useMediaQuery } from '@mui/material';
import { Menu } from '@mui/icons-material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { automationIds, agentHubPaths } from '../../util/constants';
import { buildUrlWithMerchantId } from '../../util/helpers';
import { AgentHubNavContent } from './components/AgentHubNavContent';
import { EllaLanding } from './EllaLanding';
import { useTokenRemoval } from '../../hooks';
import { useMainContent } from '../../contexts/MainContentContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { useGetAgenticSettingsQuery } from '../../store/modules/merchant/api';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';
import ApplicationLoading from '../../common/ApplicationLoading';
import appTheme from '../../styles/themes/theme';
import { NAV_DRAWER_WIDTH } from '../../AppShell/AppShell';
import { AgentHubOutletContext } from './agentHubOutletContext';

export type { AgentHubOutletContext } from './agentHubOutletContext';

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const status = (error as FetchBaseQueryError).status;
  return status === 404;
};

export const AgentHubAppShell = (): JSX.Element => {
  const { mainContentRef } = useMainContent();
  const {
    merchantId,
    hasAgentHubAccess,
    getHomePath,
    isLoading: merchantInfoLoading,
  } = useMerchant();
  const navigate = useMerchantAwareNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(appTheme.breakpoints.down('md'));
  const [drawerIsOpen, setDrawerIsOpen] = React.useState(false);
  const [isEnablingElla, setIsEnablingElla] = useState(false);
  const [hasNotFoundSettings, setHasNotFoundSettings] = useState(false);
  const [suppressOnboardingRedirect, setSuppressOnboardingRedirect] = useState(false);
  const isOnboardingRoute =
    location.pathname.toLowerCase() === agentHubPaths.ONBOARDING.toLowerCase();

  useTokenRemoval();

  const { data, error, isLoading, isFetching, isSuccess } = useGetAgenticSettingsQuery(merchantId, {
    skip: !merchantId || merchantInfoLoading,
    // Keep the known 404 in cache; do not refetch when Enable remounts child subscribers.
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  React.useEffect(() => {
    if (isNotFoundError(error)) {
      setHasNotFoundSettings(true);
      return;
    }
    if (isSuccess && data?.agents?.Ella) {
      setHasNotFoundSettings(false);
    }
  }, [error, isSuccess, data]);

  // Latch 404 across refetches: isError goes false while pending, which otherwise remounts
  // the tree and retriggers GET in a loop after Enable Ella.
  const settingsMissing =
    hasNotFoundSettings || isNotFoundError(error) || (!!data && !data.agents?.Ella);
  const isAgentHubRoot = location.pathname.toLowerCase() === agentHubPaths.AGENT_HUB.toLowerCase();
  const showLoading =
    merchantInfoLoading || ((isLoading || isFetching) && !data && !settingsMissing);

  const handleEnableElla = useCallback(() => {
    navigate(agentHubPaths.ONBOARDING, { replace: true });
  }, [navigate]);

  const handleCancelEnableElla = useCallback(() => {
    setIsEnablingElla(false);
  }, []);

  const handleEnableSuccess = useCallback(() => {
    setIsEnablingElla(false);
    setHasNotFoundSettings(false);
  }, []);

  const outletContext = useMemo<AgentHubOutletContext>(
    () => ({
      isEnablingElla: settingsMissing && isEnablingElla,
      settingsMissing,
      onCancelEnableElla: handleCancelEnableElla,
      onEnableSuccess: handleEnableSuccess,
      setSuppressOnboardingRedirect,
    }),
    [settingsMissing, isEnablingElla, handleCancelEnableElla, handleEnableSuccess],
  );

  if (!merchantInfoLoading && !hasAgentHubAccess()) {
    return <Navigate to={buildUrlWithMerchantId(getHomePath(), merchantId)} replace />;
  }

  if (isAgentHubRoot) {
    return <Navigate to={buildUrlWithMerchantId(agentHubPaths.DASHBOARD, merchantId)} replace />;
  }

  if (showLoading) {
    return <ApplicationLoading />;
  }

  if (isOnboardingRoute && !settingsMissing && !suppressOnboardingRedirect) {
    return <Navigate to={buildUrlWithMerchantId(agentHubPaths.AGENTS, merchantId)} replace />;
  }

  if (isOnboardingRoute) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          width: '100%',
          height: '100%',
          minHeight: 0,
        }}
        data-testid="agent-hub-app-shell"
      >
        <Outlet context={outletContext} />
      </Box>
    );
  }

  if (settingsMissing && !isEnablingElla) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          width: '100%',
          height: '100%',
          minHeight: 0,
        }}
        data-testid="agent-hub-app-shell"
      >
        <EllaLanding onEnable={handleEnableElla} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
      data-testid="agent-hub-app-shell"
    >
      {isMobile && (
        <IconButton
          aria-label="menu"
          onClick={() => setDrawerIsOpen(true)}
          sx={{
            position: 'absolute',
            padding: 1,
            marginLeft: 1,
            marginTop: 1,
            backgroundColor: 'secondary.light',
            borderRadius: '4px',
          }}
        >
          <Menu />
        </IconButton>
      )}
      <Drawer
        sx={{ zIndex: 10 }}
        data-testid={automationIds.SIDEBAR}
        variant={isMobile ? 'temporary' : 'permanent'}
        onClose={() => setDrawerIsOpen(false)}
        open={!isMobile || drawerIsOpen}
        PaperProps={{
          sx: {
            position: 'relative',
            width: NAV_DRAWER_WIDTH,
            zIndex: 1,
          },
        }}
      >
        <AgentHubNavContent onCloseDrawer={() => setDrawerIsOpen(false)} />
      </Drawer>
      <Box
        component="main"
        ref={mainContentRef}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Outlet context={outletContext} />
      </Box>
    </Box>
  );
};
